import React, { useEffect, useMemo, useState } from "react";

type Resource = {
  id: string;
  title: string;
  seoTitle: string;
  type: "Syllabus" | "PDF Notes" | "Demo Video" | "Test";
  subject: string;
  description: string;
  url: string;
  duration?: string;
  keywords: string;
};

type ClassData = {
  id: string;
  title: string;
  seoTitle: string;
  subtitle: string;
  badge: string;
  description: string;
  seoDescription: string;
  subjects: string[];
  resources: Resource[];
  keywords: string;
};

type FAQ = {
  q: string;
  a: string;
};

const classesData: ClassData[] = [
  {
    id: "class-7",
    title: "Class 7",
    seoTitle: "Class 7 Online Classes for Maths Science and Reasoning",
    subtitle: "School Foundation Program",
    badge: "Foundation",
    description:
      "Build strong concepts in Maths, Science and basic reasoning with chapter-wise notes, demo classes, practice tests and structured school foundation support.",
    seoDescription:
      "Class 7 online learning material with Maths notes, Science notes, syllabus, demo videos, reasoning support, practice tests and school foundation resources.",
    subjects: ["Maths", "Science", "Reasoning"],
    keywords:
      "Class 7 online classes, Class 7 Maths notes, Class 7 Science notes, Class 7 syllabus, school foundation course, Class 7 reasoning practice, Class 7 mock test",
    resources: [
      {
        id: "c7-syllabus",
        title: "Class 7 Complete Syllabus",
        seoTitle: "Class 7 Complete Syllabus with Chapter-Wise Learning Plan",
        type: "Syllabus",
        subject: "All Subjects",
        description:
          "Full yearly Class 7 roadmap with chapter-wise learning plan for Maths, Science and reasoning foundation.",
        url: "/pdf/class-7-syllabus.pdf",
        keywords:
          "Class 7 syllabus, Class 7 yearly plan, Class 7 Maths Science syllabus, foundation learning roadmap",
      },
      {
        id: "c7-maths-notes",
        title: "Integers and Fractions Notes",
        seoTitle: "Class 7 Maths Integers and Fractions PDF Notes",
        type: "PDF Notes",
        subject: "Maths",
        description:
          "Easy Class 7 Maths notes with solved examples, practice questions and revision points for Integers and Fractions.",
        url: "/pdf/class-7-maths-notes.pdf",
        keywords:
          "Class 7 Maths notes, Integers notes, Fractions notes, Class 7 Maths PDF, Maths practice questions",
      },
      {
        id: "c7-science-notes",
        title: "Nutrition in Plants Notes",
        seoTitle: "Class 7 Science Nutrition in Plants PDF Notes",
        type: "PDF Notes",
        subject: "Science",
        description:
          "Class 7 Science chapter notes with definitions, important short questions and quick revision points for Nutrition in Plants.",
        url: "/pdf/class-7-science-notes.pdf",
        keywords:
          "Class 7 Science notes, Nutrition in Plants notes, Science PDF notes, Class 7 important questions",
      },
      {
        id: "c7-science-video-1",
        title: "Nutrition in Plants Demo Class",
        seoTitle: "Class 7 Science Nutrition in Plants Demo Video",
        type: "Demo Video",
        subject: "Science",
        description:
          "Watch a Class 7 Science demo lecture before joining the foundation batch.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "18 min",
        keywords:
          "Class 7 Science demo video, Nutrition in Plants lecture, online Science class",
      },
      {
        id: "c7-science-video-2",
        title: "Science Concept Demo",
        seoTitle: "Class 7 Science Concept Demo Class Online",
        type: "Demo Video",
        subject: "Science",
        description:
          "Short demo video for Class 7 Science concept learning and foundation preparation.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "15 min",
        keywords:
          "Class 7 Science online class, Science concept demo, foundation Science lecture",
      },
      {
        id: "c7-test",
        title: "Class 7 Foundation Mock Test",
        seoTitle: "Class 7 Foundation Mock Test for Maths Science and Reasoning",
        type: "Test",
        subject: "All Subjects",
        description:
          "Basic concept test for Class 7 Maths, Science and reasoning foundation practice.",
        url: "/real-test",
        keywords:
          "Class 7 mock test, Class 7 Maths test, Class 7 Science test, foundation practice test",
      },
    ],
  },
  {
    id: "class-8",
    title: "Class 8",
    seoTitle: "Class 8 Online Tuition for Maths Science and Foundation Learning",
    subtitle: "Advanced School Foundation",
    badge: "Foundation",
    description:
      "Build a strong base for school exams and early competitive preparation with structured notes, concept videos, worksheets and topic-wise tests.",
    seoDescription:
      "Class 8 online tuition with Maths notes, Science notes, syllabus, demo classes, worksheets, reasoning support and school foundation practice.",
    subjects: ["Maths", "Science", "Reasoning"],
    keywords:
      "Class 8 online tuition, Class 8 Maths notes, Class 8 Science notes, Class 8 syllabus, school foundation, Class 8 worksheets, online classes for Class 8",
    resources: [
      {
        id: "c8-syllabus",
        title: "Class 8 Complete Syllabus",
        seoTitle: "Class 8 Complete Syllabus and Chapter-Wise Study Plan",
        type: "Syllabus",
        subject: "All Subjects",
        description:
          "Chapter-wise Class 8 syllabus and study plan for Maths, Science and reasoning foundation.",
        url: "/pdf/class-8-syllabus.pdf",
        keywords:
          "Class 8 syllabus, Class 8 study plan, Class 8 Maths Science syllabus, foundation syllabus",
      },
      {
        id: "c8-science-notes",
        title: "Force and Pressure Notes",
        seoTitle: "Class 8 Science Force and Pressure PDF Notes",
        type: "PDF Notes",
        subject: "Science",
        description:
          "Short Class 8 Science notes with important diagrams, definitions and practice questions.",
        url: "/pdf/class-8-science-notes.pdf",
        keywords:
          "Class 8 Science notes, Force and Pressure notes, Science PDF, Class 8 diagrams",
      },
      {
        id: "c8-maths-notes",
        title: "Linear Equations Notes",
        seoTitle: "Class 8 Maths Linear Equations PDF Notes",
        type: "PDF Notes",
        subject: "Maths",
        description:
          "Important formulas, solved examples and practice questions for Class 8 Linear Equations.",
        url: "/pdf/class-8-maths-notes.pdf",
        keywords:
          "Class 8 Maths notes, Linear Equations notes, Class 8 Maths PDF, solved examples",
      },
      {
        id: "c8-maths-video-1",
        title: "Linear Equations Demo Class",
        seoTitle: "Class 8 Maths Linear Equations Demo Video",
        type: "Demo Video",
        subject: "Maths",
        description:
          "Concept explanation for Class 8 Linear Equations with examples and practice support.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "22 min",
        keywords:
          "Class 8 Maths demo, Linear Equations lecture, online Maths class",
      },
      {
        id: "c8-maths-video-2",
        title: "Class 8 Maths Practice Demo",
        seoTitle: "Class 8 Maths Practice Demo Class",
        type: "Demo Video",
        subject: "Maths",
        description:
          "Demo class with Class 8 Maths practice questions and problem-solving methods.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "20 min",
        keywords:
          "Class 8 Maths practice, Maths demo class, Class 8 problem solving",
      },
      {
        id: "c8-test",
        title: "Class 8 Practice Test",
        seoTitle: "Class 8 Practice Test for Maths Science and Reasoning",
        type: "Test",
        subject: "All Subjects",
        description:
          "Topic-wise test for regular Class 8 practice and concept revision.",
        url: "/real-test",
        keywords:
          "Class 8 practice test, Class 8 mock test, Maths Science test, foundation test",
      },
    ],
  },
  {
    id: "class-9",
    title: "Class 9",
    seoTitle: "Class 9 NCERT and JEE NEET Foundation Online Course",
    subtitle: "NCERT + JEE / NEET Foundation",
    badge: "JEE / NEET",
    description:
      "Complete Class 9 syllabus with NCERT learning, Physics, Chemistry, Biology, Maths and early JEE and NEET foundation preparation.",
    seoDescription:
      "Class 9 online classes with NCERT notes, Maths, Physics, Chemistry, Biology, JEE foundation, NEET foundation, demo videos and tests.",
    subjects: ["Maths", "Physics", "Chemistry", "Biology"],
    keywords:
      "Class 9 online classes, Class 9 NCERT, Class 9 Maths, Class 9 Science, JEE foundation Class 9, NEET foundation Class 9, Physics Chemistry Biology notes",
    resources: [
      {
        id: "c9-syllabus",
        title: "Class 9 Foundation Syllabus",
        seoTitle: "Class 9 NCERT and JEE NEET Foundation Syllabus",
        type: "Syllabus",
        subject: "All Subjects",
        description:
          "NCERT plus JEE and NEET foundation roadmap for Class 9 students.",
        url: "/pdf/class-9-syllabus.pdf",
        keywords:
          "Class 9 syllabus, Class 9 foundation syllabus, JEE NEET foundation roadmap",
      },
      {
        id: "c9-physics-notes",
        title: "Motion Chapter Notes",
        seoTitle: "Class 9 Physics Motion Chapter PDF Notes",
        type: "PDF Notes",
        subject: "Physics",
        description:
          "Class 9 Physics notes with formulas, concepts, solved examples and numerical practice.",
        url: "/pdf/class-9-motion-notes.pdf",
        keywords:
          "Class 9 Physics notes, Motion chapter notes, Physics formulas, Motion numericals",
      },
      {
        id: "c9-biology-notes",
        title: "Cell Biology Notes",
        seoTitle: "Class 9 Biology Cell Chapter NCERT PDF Notes",
        type: "PDF Notes",
        subject: "Biology",
        description:
          "NCERT-based Class 9 Biology notes with important diagrams and revision questions.",
        url: "/pdf/class-9-biology-notes.pdf",
        keywords:
          "Class 9 Biology notes, Cell Biology notes, NCERT Biology, Biology diagrams",
      },
      {
        id: "c9-biology-video-1",
        title: "Cell Biology Demo Class",
        seoTitle: "Class 9 Biology Cell Demo Video",
        type: "Demo Video",
        subject: "Biology",
        description:
          "Foundation Biology demo lecture for Class 9 NCERT and NEET preparation.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "25 min",
        keywords:
          "Class 9 Biology demo, Cell Biology lecture, NEET foundation Biology",
      },
      {
        id: "c9-physics-video-1",
        title: "Motion Demo Class",
        seoTitle: "Class 9 Physics Motion Demo Video",
        type: "Demo Video",
        subject: "Physics",
        description:
          "Physics foundation demo lecture covering Motion concepts and numerical basics.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "24 min",
        keywords:
          "Class 9 Physics demo, Motion lecture, Physics numericals, JEE foundation",
      },
      {
        id: "c9-test",
        title: "Class 9 Foundation Test",
        seoTitle: "Class 9 Foundation Test for NCERT JEE and NEET Basics",
        type: "Test",
        subject: "All Subjects",
        description:
          "Practice test with NCERT and foundation-level questions for Class 9 students.",
        url: "/real-test",
        keywords:
          "Class 9 test, Class 9 foundation test, NCERT test, JEE NEET foundation test",
      },
    ],
  },
  {
    id: "class-10",
    title: "Class 10",
    seoTitle: "Class 10 CBSE Board Preparation with JEE NEET Foundation",
    subtitle: "Boards + Competitive Foundation",
    badge: "Boards",
    description:
      "Board exam preparation with JEE and NEET foundation support, sample papers, chapter-wise practice, notes and demo classes.",
    seoDescription:
      "Class 10 CBSE board exam preparation with NCERT notes, Maths, Physics, Chemistry, Biology, sample papers, mock tests and foundation support.",
    subjects: ["Maths", "Physics", "Chemistry", "Biology"],
    keywords:
      "Class 10 board preparation, CBSE Class 10 online classes, Class 10 NCERT notes, Class 10 Maths notes, Class 10 Science notes, JEE foundation Class 10, NEET foundation Class 10",
    resources: [
      {
        id: "c10-syllabus",
        title: "Class 10 Board Syllabus",
        seoTitle: "Class 10 CBSE Board Syllabus and Exam Strategy",
        type: "Syllabus",
        subject: "All Subjects",
        description:
          "Complete Class 10 board syllabus with exam strategy and chapter-wise plan.",
        url: "/pdf/class-10-syllabus.pdf",
        keywords:
          "Class 10 syllabus, CBSE Class 10 syllabus, board exam strategy, Class 10 study plan",
      },
      {
        id: "c10-maths-notes",
        title: "Quadratic Equations Notes",
        seoTitle: "Class 10 Maths Quadratic Equations PDF Notes",
        type: "PDF Notes",
        subject: "Maths",
        description:
          "Important formulas, solved questions and board-level practice for Quadratic Equations.",
        url: "/pdf/class-10-quadratic-notes.pdf",
        keywords:
          "Class 10 Maths notes, Quadratic Equations notes, CBSE Maths PDF, board exam questions",
      },
      {
        id: "c10-chem-notes",
        title: "Acids Bases and Salts Notes",
        seoTitle: "Class 10 Chemistry Acids Bases and Salts PDF Notes",
        type: "PDF Notes",
        subject: "Chemistry",
        description:
          "Important theory, reactions and board exam questions for Acids, Bases and Salts.",
        url: "/pdf/class-10-chemistry-notes.pdf",
        keywords:
          "Class 10 Chemistry notes, Acids Bases Salts notes, Chemistry reactions, board exam Chemistry",
      },
      {
        id: "c10-chem-video-1",
        title: "Acids Bases and Salts Demo",
        seoTitle: "Class 10 Chemistry Acids Bases and Salts Demo Video",
        type: "Demo Video",
        subject: "Chemistry",
        description:
          "Important board chapter explained clearly with theory and question discussion.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "28 min",
        keywords:
          "Class 10 Chemistry demo, Acids Bases Salts lecture, CBSE Chemistry class",
      },
      {
        id: "c10-maths-video-1",
        title: "Quadratic Equations Demo",
        seoTitle: "Class 10 Maths Quadratic Equations Demo Video",
        type: "Demo Video",
        subject: "Maths",
        description:
          "Board-level Class 10 Maths demo class for Quadratic Equations.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "26 min",
        keywords:
          "Class 10 Maths demo, Quadratic Equations lecture, board Maths class",
      },
      {
        id: "c10-test",
        title: "Class 10 Board Mock Test",
        seoTitle: "Class 10 CBSE Board Mock Test with Practice Questions",
        type: "Test",
        subject: "All Subjects",
        description:
          "Board-pattern mock test with practice questions for Class 10 students.",
        url: "/real-test",
        keywords:
          "Class 10 mock test, CBSE board mock test, Class 10 practice questions, board test",
      },
    ],
  },
  {
    id: "class-11",
    title: "Class 11",
    seoTitle: "Class 11 JEE NEET Preparation with Physics Chemistry Maths Biology",
    subtitle: "JEE / NEET Preparation",
    badge: "Target Batch",
    description:
      "Complete Class 11 preparation for JEE, NEET and boards with subject-wise learning material, notes, tests and demo videos.",
    seoDescription:
      "Class 11 JEE and NEET preparation with Physics, Chemistry, Maths, Biology, NCERT notes, demo classes, tests and board exam support.",
    subjects: ["Physics", "Chemistry", "Maths", "Biology"],
    keywords:
      "Class 11 JEE preparation, Class 11 NEET preparation, Class 11 Physics notes, Class 11 Chemistry notes, Class 11 Maths, Class 11 Biology, JEE Main Class 11, NEET UG Class 11",
    resources: [
      {
        id: "c11-syllabus",
        title: "Class 11 JEE / NEET Syllabus",
        seoTitle: "Class 11 JEE NEET Syllabus for Physics Chemistry Maths Biology",
        type: "Syllabus",
        subject: "All Subjects",
        description:
          "Complete Class 11 syllabus for JEE and NEET aspirants with subject-wise preparation roadmap.",
        url: "/pdf/class-11-syllabus.pdf",
        keywords:
          "Class 11 syllabus, JEE Class 11 syllabus, NEET Class 11 syllabus, PCM PCB syllabus",
      },
      {
        id: "c11-physics-notes",
        title: "Units and Dimensions Notes",
        seoTitle: "Class 11 Physics Units and Dimensions PDF Notes",
        type: "PDF Notes",
        subject: "Physics",
        description:
          "Concept notes with important questions for Class 11 Physics and JEE foundation.",
        url: "/pdf/class-11-physics-notes.pdf",
        keywords:
          "Class 11 Physics notes, Units and Dimensions notes, JEE Physics basics, Physics PDF",
      },
      {
        id: "c11-physics-video-1",
        title: "JEE Physics Demo Class",
        seoTitle: "Class 11 JEE Physics Demo Class Online",
        type: "Demo Video",
        subject: "Physics",
        description:
          "Demo class for JEE foundation students covering Class 11 Physics concepts.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "35 min",
        keywords:
          "JEE Physics demo, Class 11 Physics online class, JEE Main Physics",
      },
      {
        id: "c11-biology-video-1",
        title: "NEET Biology Demo Class",
        seoTitle: "Class 11 NEET Biology Demo Class Online",
        type: "Demo Video",
        subject: "Biology",
        description:
          "Demo lecture for NEET students covering Class 11 Biology concepts.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "34 min",
        keywords:
          "NEET Biology demo, Class 11 Biology online class, NEET UG Biology",
      },
      {
        id: "c11-test",
        title: "Class 11 JEE / NEET Test",
        seoTitle: "Class 11 JEE NEET Practice Test for Entrance Preparation",
        type: "Test",
        subject: "All Subjects",
        description:
          "Entrance-pattern practice test for Class 11 JEE and NEET aspirants.",
        url: "/real-test",
        keywords:
          "Class 11 JEE test, Class 11 NEET test, entrance practice test, PCM PCB mock test",
      },
    ],
  },
  {
    id: "class-12",
    title: "Class 12",
    seoTitle: "Class 12 Board JEE Main JEE Advanced and NEET Final Preparation",
    subtitle: "Final Year JEE / NEET",
    badge: "Final Prep",
    description:
      "Target preparation for board exams, JEE Main, JEE Advanced and NEET with notes, tests, demo lectures and final-year revision support.",
    seoDescription:
      "Class 12 board, JEE Main, JEE Advanced and NEET preparation with Physics, Chemistry, Maths, Biology notes, mock tests and demo classes.",
    subjects: ["Physics", "Chemistry", "Maths", "Biology"],
    keywords:
      "Class 12 board preparation, Class 12 JEE Main, Class 12 JEE Advanced, Class 12 NEET preparation, Class 12 Physics Chemistry Maths Biology, board exam revision, NEET final preparation",
    resources: [
      {
        id: "c12-syllabus",
        title: "Class 12 Target Syllabus",
        seoTitle: "Class 12 Board JEE NEET Target Syllabus",
        type: "Syllabus",
        subject: "All Subjects",
        description:
          "Final-year preparation roadmap for board exams, JEE Main, JEE Advanced and NEET.",
        url: "/pdf/class-12-syllabus.pdf",
        keywords:
          "Class 12 syllabus, Class 12 board syllabus, JEE NEET target syllabus, final preparation roadmap",
      },
      {
        id: "c12-chem-notes",
        title: "Electrochemistry Notes",
        seoTitle: "Class 12 Chemistry Electrochemistry PDF Notes",
        type: "PDF Notes",
        subject: "Chemistry",
        description:
          "Important theory, formulas and numerical practice for Electrochemistry.",
        url: "/pdf/class-12-chemistry-notes.pdf",
        keywords:
          "Class 12 Chemistry notes, Electrochemistry notes, Chemistry numerical practice, board Chemistry",
      },
      {
        id: "c12-biology-notes",
        title: "Human Reproduction Notes",
        seoTitle: "Class 12 Biology Human Reproduction NCERT Notes",
        type: "PDF Notes",
        subject: "Biology",
        description:
          "NCERT-based Class 12 Biology notes for NEET preparation and board revision.",
        url: "/pdf/class-12-biology-notes.pdf",
        keywords:
          "Class 12 Biology notes, Human Reproduction notes, NEET Biology NCERT, Biology revision",
      },
      {
        id: "c12-neet-video-1",
        title: "NEET Biology Demo Class",
        seoTitle: "Class 12 NEET Biology Demo Video",
        type: "Demo Video",
        subject: "Biology",
        description:
          "NCERT-based Biology demo lecture for Class 12 NEET aspirants.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "32 min",
        keywords:
          "Class 12 NEET Biology demo, NEET Biology lecture, NCERT Biology class",
      },
      {
        id: "c12-chem-video-1",
        title: "Electrochemistry Demo Class",
        seoTitle: "Class 12 Chemistry Electrochemistry Demo Video",
        type: "Demo Video",
        subject: "Chemistry",
        description:
          "Chemistry demo lecture with important questions and numerical discussion.",
        url: "https://youtu.be/IG8N5WxkhdE?si=FqGBG55sxfWNWRlQ",
        duration: "30 min",
        keywords:
          "Class 12 Chemistry demo, Electrochemistry lecture, Chemistry numerical questions",
      },
      {
        id: "c12-test",
        title: "Class 12 Target Mock Test",
        seoTitle: "Class 12 Full Syllabus Mock Test for Boards JEE and NEET",
        type: "Test",
        subject: "All Subjects",
        description:
          "Full syllabus mock test for final board exam, JEE and NEET preparation.",
        url: "/real-test",
        keywords:
          "Class 12 mock test, full syllabus test, board mock test, JEE NEET test",
      },
    ],
  },
];

const faqs: FAQ[] = [
  {
    q: "Will students get subject-wise study material?",
    a: "Yes. Students can select their class and subject to access syllabus, PDF notes, tests and demo videos related to the selected learning goal.",
  },
  {
    q: "How does the YouTube video preview work?",
    a: "You can add a normal YouTube, youtu.be or Shorts URL in the data. The component automatically converts it into an embed URL for preview.",
  },
  {
    q: "How will data come after backend integration?",
    a: "The current page uses static data. Later, the same structure can be fetched from an API with class, subject, PDF, test and video data from the database.",
  },
  {
    q: "How will PDF notes open?",
    a: "The PDF card links to the file path. You need to place the actual PDF in the public folder or serve it from the backend.",
  },
];

const getClassIdFromUrl = () => {
  if (typeof window === "undefined") return classesData[0].id;

  const params = new URLSearchParams(window.location.search);
  const classId = params.get("class");

  return classesData.some((item) => item.id === classId)
    ? String(classId)
    : classesData[0].id;
};

const getSubjectFromUrl = (classId: string) => {
  if (typeof window === "undefined") return "All Subjects";

  const params = new URLSearchParams(window.location.search);
  const subject = params.get("subject");
  const selectedClass = classesData.find((item) => item.id === classId);

  if (!selectedClass) return "All Subjects";

  return subject && selectedClass.subjects.includes(subject)
    ? subject
    : "All Subjects";
};

const getYoutubeEmbedUrl = (url: string) => {
  if (!url) return "";

  if (url.includes("youtube.com/embed/")) {
    return url;
  }

  let videoId = "";

  if (url.includes("youtube.com/watch")) {
    const query = url.split("?")[1];
    const params = new URLSearchParams(query);
    videoId = params.get("v") || "";
  }

  if (url.includes("youtu.be/")) {
    videoId = url.split("youtu.be/")[1]?.split("?")[0] || "";
  }

  if (url.includes("youtube.com/shorts/")) {
    videoId = url.split("youtube.com/shorts/")[1]?.split("?")[0] || "";
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

const getTypeStyle = (type: Resource["type"]) => {
  switch (type) {
    case "Syllabus":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "PDF Notes":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "Demo Video":
      return "bg-orange-50 text-orange-700 border-orange-100";
    case "Test":
      return "bg-purple-50 text-purple-700 border-purple-100";
    default:
      return "bg-slate-50 text-slate-700 border-slate-100";
  }
};

const getResourceIcon = (type: Resource["type"]) => {
  switch (type) {
    case "Syllabus":
      return "📘";
    case "PDF Notes":
      return "📄";
    case "Demo Video":
      return "▶️";
    case "Test":
      return "📝";
    default:
      return "📚";
  }
};

const getResourceActionText = (type: Resource["type"]) => {
  if (type === "Test") return "Start Test";
  if (type === "Demo Video") return "Watch Demo";
  if (type === "Syllabus") return "Open Syllabus";
  return "Open Notes";
};

const AllClasses: React.FC = () => {
  const [activeClassId, setActiveClassId] = useState(getClassIdFromUrl);
  const [activeSubject, setActiveSubject] = useState(() =>
    getSubjectFromUrl(getClassIdFromUrl())
  );

  useEffect(() => {
    const handleUrlChange = () => {
      const classId = getClassIdFromUrl();
      setActiveClassId(classId);
      setActiveSubject(getSubjectFromUrl(classId));
    };

    handleUrlChange();
    window.addEventListener("popstate", handleUrlChange);

    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  const activeClass =
    classesData.find((item) => item.id === activeClassId) || classesData[0];

  const subjectResources = useMemo(() => {
    return activeClass.resources.filter((resource) => {
      return (
        resource.subject === "All Subjects" ||
        activeSubject === "All Subjects" ||
        resource.subject === activeSubject
      );
    });
  }, [activeClass, activeSubject]);

  const studyMaterials = subjectResources.filter(
    (resource) => resource.type !== "Demo Video"
  );

  const demoVideos = subjectResources.filter(
    (resource) => resource.type === "Demo Video"
  );

  const mainVideo = demoVideos[0];

  useEffect(() => {
    const subjectText =
      activeSubject !== "All Subjects" ? ` ${activeSubject}` : "";

    const title = `${activeClass.title}${subjectText} Learning Material | Notes, Syllabus, Tests and Demo Classes - Mediit Academy`;

    const description =
      activeSubject === "All Subjects"
        ? activeClass.seoDescription
        : `${activeClass.title} ${activeSubject} learning material with syllabus, PDF notes, demo classes, tests, NCERT support, exam preparation resources and online learning support from Mediit Academy.`;

    const keywords = [
      activeClass.keywords,
      `${activeClass.title} ${activeSubject} notes`,
      `${activeClass.title} ${activeSubject} syllabus`,
      `${activeClass.title} ${activeSubject} demo class`,
      `${activeClass.title} ${activeSubject} test`,
      "Mediit Academy all classes",
      "online learning material",
      "PDF notes",
      "NCERT notes",
      "demo videos",
      "online mock tests",
      "student learning dashboard",
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    const subjectParam =
      activeSubject !== "All Subjects"
        ? `&subject=${encodeURIComponent(activeSubject)}`
        : "";

    canonical.setAttribute(
      "href",
      `${window.location.origin}/all-classes?class=${activeClass.id}${subjectParam}`
    );
  }, [activeClass, activeSubject]);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeClass.seoTitle} Learning Material`,
    description:
      activeSubject === "All Subjects"
        ? activeClass.seoDescription
        : `${activeClass.title} ${activeSubject} syllabus, PDF notes, demo videos and tests.`,
    url:
      activeSubject === "All Subjects"
        ? `/all-classes?class=${activeClass.id}`
        : `/all-classes?class=${activeClass.id}&subject=${activeSubject}`,
    keywords: activeClass.keywords,
    mainEntity: {
      "@type": "ItemList",
      name: `${activeClass.title} Learning Resources`,
      itemListElement: subjectResources.map((resource, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type":
            resource.type === "Demo Video" ? "VideoObject" : "LearningResource",
          name: resource.seoTitle,
          description: resource.description,
          learningResourceType: resource.type,
          educationalLevel: activeClass.title,
          about: resource.subject,
          keywords: resource.keywords,
          url: resource.url,
        },
      })),
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pageSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />

      {/* Hero Detail Section */}
      <section
        className="border-b border-slate-100 bg-white"
        aria-labelledby="all-classes-heading"
      >
        <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <div className="grid gap-10 lg:grid-cols-[1fr_460px] lg:items-start">
            {/* Left Content */}
            <div>
              <nav
                aria-label="Breadcrumb"
                className="mb-5 flex flex-wrap items-center gap-2 text-sm font-bold text-slate-400"
              >
                <a href="/" className="hover:text-blue-700">
                  Home
                </a>
                <span>&gt;</span>
                <a href="/all-classes" className="hover:text-blue-700">
                  All Classes
                </a>
                <span>&gt;</span>
                <span className="text-slate-600">{activeClass.title}</span>
                {activeSubject !== "All Subjects" && (
                  <>
                    <span>&gt;</span>
                    <span className="text-slate-600">{activeSubject}</span>
                  </>
                )}
              </nav>

              <p className="text-sm font-black uppercase tracking-[0.16em] text-orange-500">
                {activeClass.badge}
              </p>

              <h1
                id="all-classes-heading"
                className="mt-3 max-w-3xl text-4xl font-black leading-tight text-[#071f54] sm:text-5xl"
              >
                {activeClass.title}{" "}
                {activeSubject !== "All Subjects" ? activeSubject : ""}{" "}
                Learning Material
              </h1>

              <p className="mt-3 text-lg font-black text-blue-700">
                {activeClass.seoTitle}
              </p>

              <p className="mt-5 max-w-3xl text-base font-semibold leading-8 text-slate-700">
                {activeSubject === "All Subjects"
                  ? activeClass.description
                  : `${activeClass.title} ${activeSubject} learning material includes syllabus, PDF notes, practice resources, tests and demo videos in one place for easier revision and exam preparation.`}
              </p>

              <p className="sr-only">
                {activeClass.keywords}. {activeClass.seoDescription}
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                  <p className="text-2xl font-black text-blue-700">
                    {studyMaterials.length}
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-600">
                    PDFs / Notes
                  </p>
                </div>

                <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                  <p className="text-2xl font-black text-orange-700">
                    {demoVideos.length}
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-600">
                    Demo Videos
                  </p>
                </div>

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-2xl font-black text-emerald-700">
                    Online
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-600">
                    Access Mode
                  </p>
                </div>
              </div>

              {/* Class Selector */}
              <div className="mt-8">
                <h2 className="text-lg font-black text-slate-950">
                  Choose Class
                </h2>

                <div
                  className="mt-3 flex flex-wrap gap-2"
                  aria-label="Choose class learning material"
                >
                  {classesData.map((item) => (
                    <a
                      key={item.id}
                      href={`/all-classes?class=${item.id}`}
                      aria-label={`Open ${item.seoTitle}`}
                      className={`rounded-full border px-4 py-2 text-sm font-black transition-all ${
                        activeClass.id === item.id
                          ? "border-slate-950 bg-slate-950 text-white"
                          : "border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:text-blue-700"
                      }`}
                    >
                      {item.title}
                    </a>
                  ))}
                </div>
              </div>

              {/* Subject Chips */}
              <div className="mt-8">
                <h2 className="text-lg font-black text-slate-950">
                  Available Subjects
                </h2>

                <div
                  className="mt-3 flex flex-wrap gap-2"
                  aria-label="Choose subject learning material"
                >
                  <a
                    href={`/all-classes?class=${activeClass.id}`}
                    className={`rounded-full border px-4 py-2 text-sm font-black transition-all ${
                      activeSubject === "All Subjects"
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:text-blue-700"
                    }`}
                  >
                    All Subjects
                  </a>

                  {activeClass.subjects.map((subject) => (
                    <a
                      key={subject}
                      href={`/all-classes?class=${
                        activeClass.id
                      }&subject=${encodeURIComponent(subject)}`}
                      aria-label={`Open ${activeClass.title} ${subject} learning material`}
                      className={`rounded-full border px-4 py-2 text-sm font-black transition-all ${
                        activeSubject === subject
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:text-blue-700"
                      }`}
                    >
                      {subject}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Video Card */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/70">
              <div className="overflow-hidden rounded-t-2xl bg-slate-100">
                {mainVideo ? (
                  <iframe
                    className="aspect-video w-full"
                    src={getYoutubeEmbedUrl(mainVideo.url)}
                    title={mainVideo.seoTitle}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center bg-slate-100">
                    <div className="text-center">
                      <p className="text-4xl">▶️</p>
                      <p className="mt-2 text-sm font-bold text-slate-500">
                        Demo video coming soon
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 border-b border-slate-100">
                <div className="border-r border-slate-100 p-4">
                  <p className="text-xs font-bold text-slate-400">Type</p>
                  <p className="mt-1 text-sm font-black text-slate-800">
                    Pre-Recorded
                  </p>
                </div>

                <div className="border-r border-slate-100 p-4">
                  <p className="text-xs font-bold text-slate-400">Subject</p>
                  <p className="mt-1 text-sm font-black text-slate-800">
                    {activeSubject}
                  </p>
                </div>

                <div className="p-4">
                  <p className="text-xs font-bold text-slate-400">Duration</p>
                  <p className="mt-1 text-sm font-black text-slate-800">
                    {mainVideo?.duration || "Demo"}
                  </p>
                </div>
              </div>

              <div className="p-4">
                <a
                  href="/login"
                  aria-label={`Enroll for ${activeClass.title} online learning program`}
                  className="block rounded-xl bg-yellow-400 px-5 py-3 text-center text-sm font-black text-slate-950 transition-all hover:bg-yellow-300"
                >
                  Enroll for Free
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About + Study Material */}
      <section className="mx-auto max-w-[1280px] px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1fr_420px]">
          {/* Left About */}
          <div>
            <h2 className="text-3xl font-black text-[#071f54]">
              About {activeClass.title} Course Material
            </h2>

            <p className="mt-4 text-base font-medium leading-8 text-slate-700">
              This learning detail page helps students access class-wise and
              subject-wise syllabus, PDF notes, practice material, tests and demo
              videos in one clean place. Students can use these resources for
              school exam preparation, NCERT revision, board exam practice, JEE
              foundation and NEET foundation learning.
            </p>

            <p className="mt-4 text-base font-medium leading-8 text-slate-700">
              After backend integration, the same structure can be connected with
              database records and filtered by class, subject, course, teacher or
              batch. For now, the UI is ready with static data and can be
              connected with API endpoints later.
            </p>

            <div className="mt-8">
              <h3 className="text-2xl font-black text-slate-950">
                Syllabus, Notes and Practice Material
              </h3>

              {studyMaterials.length > 0 ? (
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {studyMaterials.map((resource) => (
                    <a
                      key={resource.id}
                      href={resource.url}
                      title={resource.keywords}
                      aria-label={`Open ${resource.seoTitle}`}
                      className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg hover:shadow-slate-200/70"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-2xl">
                          {getResourceIcon(resource.type)}
                        </div>

                        <span
                          className={`rounded-full border px-3 py-1 text-xs font-black ${getTypeStyle(
                            resource.type
                          )}`}
                        >
                          {resource.type}
                        </span>
                      </div>

                      <p className="mt-4 text-xs font-black uppercase tracking-[0.14em] text-blue-600">
                        {resource.subject}
                      </p>

                      <h4 className="mt-2 text-lg font-black text-slate-950 group-hover:text-blue-700">
                        {resource.title}
                      </h4>

                      <p className="mt-1 text-sm font-bold text-blue-700">
                        {resource.seoTitle}
                      </p>

                      <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
                        {resource.description}
                      </p>

                      <p className="sr-only">{resource.keywords}</p>

                      <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                        <span className="text-sm font-black text-[#07111b]">
                          {getResourceActionText(resource.type)}
                        </span>
                        <span className="text-xl text-blue-600 transition-transform group-hover:translate-x-1">
                          →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                  <h4 className="text-lg font-black text-slate-900">
                    No notes found
                  </h4>
                  <p className="mt-2 text-sm font-medium text-slate-500">
                    Notes will appear here after the selected subject resources
                    are added from the backend.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Demo List */}
          <aside className="h-fit rounded-3xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="text-2xl font-black text-slate-950">
              Demo Classes
            </h3>

            <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
              Students can watch demo videos before joining the batch and
              understand the teaching style, subject flow and learning approach.
            </p>

            <div className="mt-5 space-y-4">
              {demoVideos.length > 0 ? (
                demoVideos.map((video) => (
                  <article
                    key={video.id}
                    title={video.keywords}
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  >
                    <iframe
                      className="aspect-video w-full"
                      src={getYoutubeEmbedUrl(video.url)}
                      title={video.seoTitle}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />

                    <div className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 text-xs font-black text-orange-700">
                          Demo Video
                        </span>

                        {video.duration && (
                          <span className="text-xs font-black text-slate-500">
                            {video.duration}
                          </span>
                        )}
                      </div>

                      <h4 className="mt-3 text-base font-black text-slate-950">
                        {video.title}
                      </h4>

                      <p className="mt-1 text-sm font-bold text-blue-700">
                        {video.seoTitle}
                      </p>

                      <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
                        {video.description}
                      </p>

                      <p className="sr-only">{video.keywords}</p>
                    </div>
                  </article>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                  <p className="text-3xl">🎬</p>
                  <h4 className="mt-3 text-lg font-black text-slate-900">
                    No demo video found
                  </h4>
                  <p className="mt-2 text-sm font-medium text-slate-500">
                    The demo preview will appear here after a YouTube link is
                    added for this class and subject.
                  </p>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-100 bg-[#f7fbff]">
        <div className="mx-auto max-w-[1280px] px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-600">
              Student Help
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-950">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {faqs.map((faq) => (
              <article
                key={faq.q}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-lg font-black text-slate-950">{faq.q}</h3>

                <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                  {faq.a}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default AllClasses; 